import React from 'react';
import { Grid, Typography, Link, Box } from '@mui/material';
import StepLabel from '@material-ui/core/StepLabel';
import { Page, Content, ContentHeader } from '@backstage/core-components';
import {
  defaultGenerateStepper,
  ImportStepper,
  ImportStepperProps,
} from '@backstage/plugin-catalog-import';
import { Breadcrumbs } from '@internal/backstage-plugin-global-header';
import { StepAnalyzeUrl } from './StepAnalyzeUrl';

const generateStepper: ImportStepperProps['generateStepper'] = (
  flow,
  defaults,
) => ({
  ...defaultGenerateStepper(flow, defaults),
  analyze: (state, { apis }) => ({
    stepLabel: <StepLabel>Select URL</StepLabel>,
    content: (
      <StepAnalyzeUrl
        analysisUrl={state.analysisUrl}
        onAnalysis={state.onAnalysis}
        disablePullRequest={!apis.catalogImportApi.preparePullRequest}
      />
    ),
  }),
});

const RegisterComponentPage = () => {
  return (
    <Page themeId="home">
      <Content>
        <Breadcrumbs />
        <Grid
          paddingTop={5}
          paddingLeft={3.5}
          container
          spacing={4}
          direction="row-reverse"
        >
          <Grid item xs={12} md={4} lg={6} xl={8}>
            <Box
              sx={{
                backgroundColor: '#ffffff',
                padding: '32px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                marginLeft: { xs: '0', md: '20px' },
                borderRadius: '12px',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 6px 24px rgba(0, 0, 0, 0.12)',
                  transform: 'translateY(-2px)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '4px',
                  background: 'linear-gradient(90deg, #3f51b5, #7986cb)',
                },
              }}
            >
              <ContentHeader
                title="Start tracking your components"
                description="Integrate your existing components into the COE Marketplace ecosystem"
              />
              <Box mb={3} />

              <Box mb={5}>
                <Typography
                  variant="h6"
                  paragraph
                  sx={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: '#303f9f',
                    marginTop: '20px',
                    paddingBottom: '8px',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: '40px',
                      height: '3px',
                      backgroundColor: '#7986cb',
                    },
                  }}
                >
                  Register an existing component
                </Typography>
                <Typography
                  paragraph
                  sx={{
                    color: '#424242',
                    fontSize: '1rem',
                    lineHeight: 1.6,
                  }}
                >
                  Enter the URL to your source code repository to add it to COE
                  Marketplace.
                </Typography>

                <Typography
                  variant="h6"
                  paragraph
                  sx={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: '#303f9f',
                    marginTop: '28px',
                    paddingBottom: '8px',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: '40px',
                      height: '3px',
                      backgroundColor: '#7986cb',
                    },
                  }}
                >
                  Link to an existing entity file
                </Typography>
                <Typography
                  paragraph
                  sx={{
                    color: '#424242',
                    fontSize: '1rem',
                    lineHeight: 1.6,
                  }}
                >
                  Example:{' '}
                  <Link
                    href="https://git.cnvrmedia.net/projects/GPSA/repos/gpsa-ops-assist-gui/browse/catalogue-info.yaml?at=refs%2Fheads%2Fbackstage"
                    target="_blank"
                    rel="noopener"
                    sx={{
                      color: '#3f51b5',
                      textDecoration: 'none',
                      fontWeight: 500,
                      transition: 'color 0.2s',
                      '&:hover': {
                        color: '#7986cb',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    https://git.cnvrmedia.net/projects/GPSA/repos/gpsa-ops-assist-gui/browse/catalogue-info.yaml?at=refs%2Fheads%2Fbackstage
                  </Link>
                </Typography>
                <Box
                  sx={{
                    backgroundColor: '#f5f7ff',
                    padding: '16px',
                    borderRadius: '8px',
                    borderLeft: '4px solid #3f51b5',
                    marginTop: '12px',
                    marginBottom: '20px',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#424242',
                      fontSize: '0.95rem',
                      lineHeight: 1.6,
                    }}
                  >
                    The wizard analyzes the file, previews the entities, and
                    adds them to the COE Marketplace catalog.
                  </Typography>
                </Box>

                <Typography
                  variant="h6"
                  paragraph
                  sx={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: '#303f9f',
                    marginTop: '28px',
                    paddingBottom: '8px',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: '40px',
                      height: '3px',
                      backgroundColor: '#7986cb',
                    },
                  }}
                >
                  Link to a repository
                </Typography>
                <Typography
                  paragraph
                  sx={{
                    color: '#424242',
                    fontSize: '1rem',
                    lineHeight: 1.6,
                  }}
                >
                  Example:{' '}
                  <Link
                    href="https://git.cnvrmedia.net/projects/GPSA/repos/gpsa-ops-assist-gui"
                    target="_blank"
                    rel="noopener"
                    sx={{
                      color: '#3f51b5',
                      textDecoration: 'none',
                      fontWeight: 500,
                      transition: 'color 0.2s',
                      '&:hover': {
                        color: '#7986cb',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    https://git.cnvrmedia.net/projects/GPSA/repos/gpsa-ops-assist-gui
                  </Link>
                </Typography>
                <Box
                  sx={{
                    backgroundColor: '#f5f7ff',
                    padding: '16px',
                    borderRadius: '8px',
                    borderLeft: '4px solid #3f51b5',
                    marginTop: '12px',
                    marginBottom: '20px',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#424242',
                      fontSize: '0.95rem',
                      lineHeight: 1.6,
                    }}
                  >
                    The wizard discovers all catalog-info.yaml files in the
                    repository, previews the entities, and adds them to the COE
                    Marketplace catalog.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    backgroundColor: '#fff8e1',
                    padding: '16px',
                    borderRadius: '8px',
                    borderLeft: '4px solid #ffb300',
                    marginTop: '24px',
                    marginBottom: '10px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: '#fff3c4',
                      borderRadius: '50%',
                      width: '36px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '16px',
                      flexShrink: 0,
                    }}
                  >
                    i
                  </Box>
                  <Typography
                    sx={{
                      color: '#5d4037',
                      fontSize: '0.95rem',
                      lineHeight: 1.6,
                    }}
                  >
                    If no entities are found, the wizard will prepare a Pull
                    Request that adds an example catalog-info.yaml and prepares
                    the COE Marketplace catalog to load all entities as soon as
                    the Pull Request is merged.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={8} lg={6} xl={4}>
            <Box
              sx={{
                backgroundColor: '#ffffff',
                padding: '24px',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              }}
            >
              <ImportStepper generateStepper={generateStepper} />
            </Box>
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};

export default RegisterComponentPage;
